import { Component, Input, Output, EventEmitter, ChangeDetectorRef, ElementRef, OnChanges, OnDestroy, SimpleChanges, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

export interface AssetHeaderConfig {
  breadcrumbs?: { label: string; route?: string }[];
  title: string;
  description: string;
  /** When set (e.g. discussion header), shown as "Created at {date}" with smaller font */
  createdAt?: string;
  iconPath?: string; // Path to decorative icons (e.g., leaf icons)
  assetId?: string; // Asset ID to copy
}

@Component({
  selector: 'app-asset-header',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './asset-header.component.html',
  styleUrl: './asset-header.component.scss',
})
export class AssetHeaderComponent implements OnChanges, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  @Input() config!: AssetHeaderConfig;
  /** TANUH catalogue pages: teal primary + light secondary header surface */
  @Input() variant: 'default' | 'tanuh' = 'tanuh';
  @Input() isLoggedIn: boolean = false;
  @Input() isBookmarked: boolean = false;
  @Input() isLiked: boolean = false;
  @Input() isPinned: boolean = false;
  @Input() showActions: boolean = false; // Controls visibility of Bookmark/Like/Pin buttons (only for detail pages)
  @Input() showLikeButton: boolean = true; // When false, hide Like button (e.g. challenge and discussion pages)
  @Input() showPinButton: boolean = false; // Pin icon only for discussions
  @Input() showVoteButtons: boolean = false; // Upvote/downvote icons only for discussion detail page
  @Input() userVote: 'up' | 'down' | null = null; // Current user vote state (discussion only)
  @Input() voteCount: number = 0; // Display vote count (discussion only)
  
  @Output() bookmarkClick = new EventEmitter<void>();
  @Output() likeClick = new EventEmitter<void>();
  @Output() pinClick = new EventEmitter<void>();
  @Output() upvoteClick = new EventEmitter<void>();
  @Output() downvoteClick = new EventEmitter<void>();
  
  copySuccess: boolean = false;
  descriptionExpanded: boolean = false;
  descriptionOverflows: boolean = false; // True when the clamped description is cut off (shows Read more/less)

  private descriptionEl?: HTMLElement;
  private descriptionObserver?: ResizeObserver;

  @ViewChild('descriptionEl')
  set descriptionRef(ref: ElementRef<HTMLElement> | undefined) {
    this.descriptionObserver?.disconnect();
    this.descriptionEl = ref?.nativeElement;
    if (this.descriptionEl && typeof ResizeObserver !== 'undefined') {
      this.descriptionObserver = new ResizeObserver(() => this.measureDescription());
      this.descriptionObserver.observe(this.descriptionEl);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && !changes['config'].firstChange) {
      this.descriptionExpanded = false;
      setTimeout(() => this.measureDescription());
    }
  }

  ngOnDestroy(): void {
    this.descriptionObserver?.disconnect();
  }

  toggleDescription(): void {
    this.descriptionExpanded = !this.descriptionExpanded;
  }

  private measureDescription(): void {
    // While expanded nothing is clamped, so keep the toggle as it is
    if (!this.descriptionEl || this.descriptionExpanded) {
      return;
    }
    const overflows = this.descriptionEl.scrollHeight > this.descriptionEl.clientHeight + 1;
    if (overflows !== this.descriptionOverflows) {
      this.descriptionOverflows = overflows;
      this.cdr.detectChanges();
    }
  }

  copyAssetId(): void {
    if (this.config.assetId) {
      navigator.clipboard.writeText(this.config.assetId).then(() => {
        this.copySuccess = true;
        this.cdr.detectChanges();
        setTimeout(() => {
          this.copySuccess = false;
          this.cdr.detectChanges();
        }, 2000);
      }).catch(err => {
        console.error('Failed to copy asset ID:', err);
      });
    }
  }

  onBookmarkClick(): void {
    this.bookmarkClick.emit();
  }

  onLikeClick(): void {
    this.likeClick.emit();
  }

  onPinClick(): void {
    this.pinClick.emit();
  }

  onUpvoteClick(): void {
    this.upvoteClick.emit();
  }

  onDownvoteClick(): void {
    this.downvoteClick.emit();
  }

  get pageIcon(): string {
    const hay = [
      this.config?.title ?? '',
      ...(this.config?.breadcrumbs?.map((crumb) => crumb.label) ?? []),
    ]
      .join(' ')
      .toLowerCase();

    if (hay.includes('reference')) {
      return 'dataset';
    }
    if (hay.includes('dataset')) {
      return 'dataset';
    }
    if (hay.includes('model')) {
      return 'model';
    }
    if (hay.includes('discussion')) {
      return 'discussion';
    }
    if (hay.includes('challenge')) {
      return 'challenge';
    }
    if (hay.includes('knowledge')) {
      return 'knowledge';
    }
    if (hay.includes('sandbox')) {
      return 'sandbox';
    }
    if (hay.includes('leader')) {
      return 'leaderboard';
    }
    if (hay.includes('submission')) {
      return 'submit';
    }
    if (hay.includes('librar')) {
      return 'library';
    }
    if (hay.includes('toolkit')) {
      return 'toolkit';
    }
    if (hay.includes('usecase') || hay.includes('use case') || hay.includes('application')) {
      return 'usecase';
    }
    if (hay.includes('manual')) {
      return 'knowledge';
    }
    return 'default';
  }
}

